import { useNavigate } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../../presentation-components/miscellaneous-components/table-action-buttons';
import { LoadingPage } from '../../loading-page';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { IInflationIndex } from '../../../../shared/services/inflation-index/inflation-index.model';
import InflationIndexService, { ICpiCoverage } from '../../../../shared/services/inflation-index/inflation-index.service';
import { AddInflationIndexDialog } from '../../../dialogs/super-admin/add-inflation-index-dialog';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';

const inflationIndexService = new InflationIndexService();

export const SuperAdminInflationIndicesPage = () => {
  const navigate = useNavigate();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const [indices, setIndices] = useState<IInflationIndex[]>([]);
  const [coverage, setCoverage] = useState<ICpiCoverage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [change, setChange] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    inflationIndexService.getAll()
      .then(setIndices)
      .catch(console.error)
      .finally(() => setIsLoading(false));
    inflationIndexService.getCoverage()
      .then(setCoverage)
      .catch(() => setCoverage([]));
  }, [change]);

  const handleSetDefault = async (id: string, currencyCode: string) => {
    try {
      await inflationIndexService.update(id, { isDefault: true });
      setChange(c => c + 1);
      userNotificationsContext.openSnackbar({ open: true, message: `Default index set for ${currencyCode}` });
    } catch (e) {
      console.error(e);
    }
  };

  const headCells: IHeadCell[] = [
    { id: 'name', label: 'Name', numeric: false, sortable: false },
    { id: 'currencyCode', label: 'Currency', numeric: false, sortable: false },
    { id: 'source', label: 'Source', numeric: false, sortable: false },
    { id: 'isDefault', label: 'Default', numeric: false, sortable: false },
    { id: 'isActive', label: 'Status', numeric: false, sortable: false },
    { id: 'yearCount', label: 'Year Values', numeric: true, sortable: false },
    { id: 'actions', label: '', numeric: false, sortable: false },
  ];

  const rows = indices.map(index => ({
    id: index.id,
    name: index.name,
    currencyCode: index.currencyCode,
    source: index.source,
    isDefault: index.isDefault ? <Chip label="Default" color="primary" size="small" /> : (
      <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleSetDefault(index.id, index.currencyCode); }}>
        Set default
      </Button>
    ),
    isActive: <Chip label={index.isActive ? 'Active' : 'Inactive'} color={index.isActive ? 'success' : 'default'} size="small" />,
    yearCount: index.values?.length ?? '—',
    actions: (
      <TableActionButtons
        id={index.id}
        includeView={true}
        onView={() => navigate(`/super-admin/inflation-indices/${index.id}`)}
      />
    ),
  }));

  return (
    <PageOuterContainer>
      <PageHeading
        title="Inflation Indices"
        includeActionButton={true}
        actionButtonText="Add Index"
        actionButtonHandler={() => setIsAddDialogOpen(true)}
        actionButtonIcon={IconName.Add}
        includeBackButton={true}
        backButtonLink="/super-admin"
        backButtonText="Back to super admin"
      />
      <LoadingPage pageReady={!isLoading}>
        {/* FY-8: CPI coverage against spend factor price years and observed spend
            calculation years for each index's currency. Missing years are bridged
            with the closest available year, so this prompts making them exact. */}
        {coverage.some(c => c.missingYears.length > 0) && (
          <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'warning.main', borderRadius: '10px' }}>
            <Typography variant='subtitle2' sx={{ color: 'text.primary', mb: 0.5 }}>CPI coverage gaps</Typography>
            {coverage.filter(c => c.missingYears.length > 0).map(c => (
              <Typography key={c.indexId} variant='body2' sx={{ color: 'text.secondary' }}>
                {c.currencyCode} ({c.indexName}): no value for year(s) {c.missingYears.join(', ')}. Spend-based inflation adjustments for those years are approximated using the closest available year; add the values to make them exact.
              </Typography>
            ))}
          </Box>
        )}
        <TableDisplay
          headCells={headCells}
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          onRowClick={(id) => navigate(`/super-admin/inflation-indices/${id}`)}
        />
      </LoadingPage>
      <AddInflationIndexDialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onSuccess={() => { setIsAddDialogOpen(false); setChange(c => c + 1); }}
      />
    </PageOuterContainer>
  );
};
